import { useState, useEffect } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface MarkdownViewerProps {
  documentPath: string;
  documentTitle: string;
  onBack: () => void;
}

export default function MarkdownViewer({
  documentPath,
  documentTitle,
  onBack
}: MarkdownViewerProps) {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    fetch(documentPath)
      .then(res => {
        if (!res.ok) {
          throw new Error('Failed to load document');
        }
        return res.text();
      })
      .then(text => {
        setContent(text);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [documentPath]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-neutral-600">Loading research document...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Research Library
        </button>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-600 font-medium mb-2">Failed to load document</p>
          <p className="text-red-500 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto pb-12">
      {/* Back button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-blue-400 hover:text-blue-300 mb-8 transition-colors font-medium"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Research Library
      </button>

      {/* Document header with white background */}
      <div className="bg-white rounded-2xl p-8 mb-8 shadow-sm">
        <h1 className="text-4xl font-bold text-neutral-900 mb-2">
          {documentTitle}
        </h1>
        <div className="h-1 w-20 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full"></div>
      </div>

      {/* Markdown content with white background */}
      <div className="bg-white rounded-2xl p-8 sm:p-12 shadow-sm">
        <article className="prose prose-lg prose-neutral max-w-none
          prose-headings:font-bold prose-headings:text-neutral-900
          prose-h1:text-4xl prose-h1:mb-6 prose-h1:border-b prose-h1:border-neutral-200 prose-h1:pb-4
          prose-h2:text-3xl prose-h2:mb-4 prose-h2:mt-10 prose-h2:border-b prose-h2:border-neutral-200 prose-h2:pb-2
          prose-h3:text-2xl prose-h3:mb-3 prose-h3:mt-8
          prose-h4:text-xl prose-h4:mb-2 prose-h4:mt-6
          prose-p:mb-4 prose-p:leading-relaxed prose-p:text-neutral-700
          prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline
          prose-strong:text-neutral-900 prose-strong:font-semibold
          prose-code:bg-neutral-100 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-sm prose-code:font-mono prose-code:text-neutral-800 prose-code:before:content-[''] prose-code:after:content-['']
          prose-pre:!bg-neutral-50 prose-pre:!text-neutral-900 prose-pre:!p-6 prose-pre:!rounded-xl prose-pre:overflow-x-auto prose-pre:!my-6 prose-pre:!border prose-pre:!border-neutral-200
          [&_pre_code]:!bg-transparent [&_pre_code]:!p-0 [&_pre_code]:!text-neutral-900 [&_pre_code]:!text-base [&_pre_code]:leading-relaxed [&_pre_code]:whitespace-pre-wrap [&_pre_code]:break-words [&_pre_code]:block
          prose-ul:my-4 prose-ul:ml-6 prose-ul:list-disc
          prose-ol:my-4 prose-ol:ml-6 prose-ol:list-decimal
          prose-li:mb-2 prose-li:text-neutral-700
          prose-blockquote:border-l-4 prose-blockquote:border-blue-500 prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-neutral-600 prose-blockquote:my-6
          prose-table:w-full prose-table:my-6 prose-table:border-collapse
          prose-th:bg-neutral-100 prose-th:border prose-th:border-neutral-300 prose-th:px-4 prose-th:py-2 prose-th:text-left prose-th:font-semibold prose-th:text-neutral-900
          prose-td:border prose-td:border-neutral-300 prose-td:px-4 prose-td:py-2 prose-td:text-neutral-700
          prose-hr:my-8 prose-hr:border-neutral-300
          prose-img:rounded-lg prose-img:shadow-md
        ">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              a: ({ node, ...props }) => (
                <a {...props} target="_blank" rel="noopener noreferrer" />
              )
            }}
          >
            {content}
          </ReactMarkdown>
        </article>
      </div>

      {/* Scroll to top button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="mt-12 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
      >
        Back to Top
      </button>
    </div>
  );
}
